import { memo, useState, useCallback, useMemo } from 'react';
import { CaseReportMedicalList } from '@/components/casereport/CaseReportMedicalList';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { DateTimePicker } from '@/components/DateTimePicker';
import { App } from '@/services/app-compat';
import { CaseRpt } from '@/services/case-report-service';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getInsecureUUID = _wg('getInsecureUUID');

function emptyPatientForm() {
  return { tempid: null as any, personId: null as any, personName: '', hospitalized: null as any, symptoms: '' };
}

// Label map for medical checklist boolean fields
const MEDICAL_LABELS: Record<string, string> = {
  heartProblems: 'Heart Problems?',
  highBloodPressure: 'High Blood Pressure?',
  hypertensionPatientHistory: 'Hypertension?',
  diabetes: 'Diabetes?',
  medications: 'Prescription Medication?',
  otcMedications: 'OTC / Non-prescribed Medication?',
  vitamins: 'Vitamins, Homeopathics, Herbal Medicines, or Supplements?',
  allergicToMedications: 'Allergic to Any Medications?',
  majorSurgeries: 'Major Surgeries or Illnesses?',
  abdominalPain: 'Abdominal Pain?',
  nausea: 'Nausea or Vomiting?',
  bloating: 'Bloating, Gas, or Indigestion?',
  heartburn: 'Heartburn?',
  ulcer: 'Ulcer?',
  difficultySwallowing: 'Difficulty Swallowing?',
  jaundice: 'Jaundice?',
  liverDisease: 'Liver Disease?',
  gallBladderProblems: 'Gallbladder Problems?',
  pancreatitis: 'Pancreatitis?',
  bowelHabits: 'Change in Bowel Habits?',
  bloodyStool: 'Black or Bloody Stool?',
  colonCancer: 'Colon Cancer or Colon Polyps?',
  hemorrhoids: 'Hemorrhoids?',
  chestPain: 'Chest Pain or Tightness?',
  palpitations: 'Palpitations?',
  swollenLegs: 'Swollen Legs or Feet?',
  hypertensionCardio: 'Hypertension?',
  hyperlipidemia: 'Hyperlipidemia?',
  angina: 'Angina?',
  rheumaticFever: 'Rheumatic Fever?',
  claudication: 'Claudication or Leg Pain on Walking?',
  bloodClots: 'Blood Clots?',
  varicoseVeins: 'Varicose Veins?',
  urinaryProblems: 'Urinary Problems?',
  bloodInUrine: 'Blood in Urine?',
  kidneyStones: 'Kidney Stones?',
  urinaryInfections: 'Urinary Infections?',
  sexualDysfunction: 'Sexual Dysfunction?',
  useContraceptives: 'Use Contraceptives?',
};

// Checklist section definitions per type
const PATIENT_HISTORY_SECTIONS = [
  {
    title: 'Medical History',
    fields: ['heartProblems', 'highBloodPressure', 'hypertensionPatientHistory', 'diabetes'],
  },
  {
    title: 'Currently Taking',
    fields: ['medications', 'otcMedications', 'vitamins', 'allergicToMedications'],
    conditionalTextFields: {
      medications: 'medicationsTxt',
      otcMedications: 'otcMedicationsTxt',
      vitamins: 'vitaminsTxt',
      allergicToMedications: 'allergicToMedicationsTxt',
    } as Record<string, string>,
  },
  {
    title: 'Other History',
    fields: ['majorSurgeries'],
    conditionalTextFields: { majorSurgeries: 'majorSurgeriesTxt' } as Record<string, string>,
    hasLastAte: true,
  },
];

const ABDOMINAL_SECTIONS = [
  {
    title: 'Symptoms',
    fields: ['abdominalPain', 'nausea', 'bloating', 'heartburn', 'ulcer', 'difficultySwallowing', 'jaundice'],
  },
  {
    title: 'Conditions',
    fields: ['liverDisease', 'gallBladderProblems', 'pancreatitis', 'bowelHabits', 'bloodyStool', 'colonCancer', 'hemorrhoids'],
  },
  { title: 'Additional Notes', noteField: 'abdominalTxt' },
];

const CARDIOVASCULAR_SECTIONS = [
  {
    title: 'Symptoms & Conditions',
    fields: ['chestPain', 'palpitations', 'swollenLegs', 'hypertensionCardio', 'hyperlipidemia', 'angina', 'rheumaticFever', 'claudication', 'bloodClots', 'varicoseVeins'],
  },
  { title: 'Additional Notes', noteField: 'cardiovascularTxt' },
];

const GENITAL_URINARY_SECTIONS = [
  {
    title: 'Symptoms & Conditions',
    fields: ['urinaryProblems', 'bloodInUrine', 'kidneyStones', 'urinaryInfections', 'sexualDysfunction', 'useContraceptives'],
  },
  { title: 'Additional Notes', noteField: 'urinaryTxt' },
];

const ALL_BOOL_FIELDS = Object.keys(MEDICAL_LABELS);
const ALL_TEXT_FIELDS = [
  'medicationsTxt', 'otcMedicationsTxt', 'vitaminsTxt', 'allergicToMedicationsTxt',
  'majorSurgeriesTxt', 'abdominalTxt', 'cardiovascularTxt', 'urinaryTxt',
];

function emptyMedicalChecklistForm(): Record<string, any> {
  const form: Record<string, any> = {};
  ALL_BOOL_FIELDS.forEach(f => { form[f] = null; });
  ALL_TEXT_FIELDS.forEach(f => { form[f] = ''; });
  form.lastAte = null;
  return form;
}

function copyMedicalFieldsFromReport(report: any): Record<string, any> {
  const form: Record<string, any> = {};
  ALL_BOOL_FIELDS.forEach(f => { form[f] = report[f] ?? null; });
  ALL_TEXT_FIELDS.forEach(f => { form[f] = report[f] || ''; });
  form.lastAte = report.lastAte ?? null;
  return form;
}

function applyMedicalFieldsToReport(report: any, form: Record<string, any>) {
  [...ALL_BOOL_FIELDS, ...ALL_TEXT_FIELDS, 'lastAte'].forEach(f => { report[f] = form[f]; });
}

interface Props {
  medicalReports: any[];
  setMedicalReports: (m: any[]) => void;
  involvedPersonOptions: { id: any; name: string }[];
  isReadOnly: boolean;
}

export const CaseReportMedicalSection = memo(function CaseReportMedicalSection({
  medicalReports, setMedicalReports, involvedPersonOptions, isReadOnly,
}: Props) {
  // Patient modal state
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [patientForm, setPatientForm] = useState(emptyPatientForm);

  // Medical checklist modal state
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklistForm, setChecklistForm] = useState<Record<string, any>>(emptyMedicalChecklistForm);
  const [checklistType, setChecklistType] = useState('');
  const [editingMedicalReport, setEditingMedicalReport] = useState<any>(null);

  const checklistTitle = useMemo(() => {
    switch (checklistType) {
      case 'patientHistory': return 'Patient History';
      case 'abdominalDigestive': return 'Abdominal / Digestive';
      case 'cardiovascular': return 'Cardiovascular';
      case 'genitalUrinary': return 'Genital / Urinary';
      default: return 'Medical Checklist';
    }
  }, [checklistType]);

  const checklistSections = useMemo(() => {
    switch (checklistType) {
      case 'patientHistory': return PATIENT_HISTORY_SECTIONS;
      case 'abdominalDigestive': return ABDOMINAL_SECTIONS;
      case 'cardiovascular': return CARDIOVASCULAR_SECTIONS;
      case 'genitalUrinary': return GENITAL_URINARY_SECTIONS;
      default: return [];
    }
  }, [checklistType]);

  const openPatientModal = useCallback((patient?: any) => {
    if (patient) {
      setPatientForm({
        tempid: patient.tempid, personId: patient.patient,
        personName: patient.personName || '',
        hospitalized: patient.hospitalized ? String(patient.hospitalized) : null,
        symptoms: patient.symptomsOrInjuriesTxt || patient.symptoms || '',
      });
    } else {
      setPatientForm(emptyPatientForm());
    }
    setShowPatientModal(true);
  }, []);

  const savePatient = useCallback(() => {
    const form = patientForm;
    if (!form.personId) { App.showPopupNotification('Patient is required.', 'error'); return; }
    if (!form.hospitalized) { App.showPopupNotification('Hospitalized status is required.', 'error'); return; }

    const dup = CaseRpt.currentMDTRpt.medicalReports.findIndex((n: any) =>
      n.patient?.toString() === form.personId.toString() && (!form.tempid || n.tempid !== form.tempid));
    if (dup > -1) { App.showPopupNotification('This person has already been added as a patient.', 'warning'); return; }

    const entry = {
      tempid: form.tempid || getInsecureUUID(), patient: form.personId,
      personName: form.personName, hospitalized: parseInt(form.hospitalized),
      symptomsOrInjuriesTxt: form.symptoms,
    };

    if (form.tempid) {
      const idx = CaseRpt.currentMDTRpt.medicalReports.findIndex((p: any) => p.tempid === form.tempid);
      if (idx > -1) Object.assign(CaseRpt.currentMDTRpt.medicalReports[idx], entry);
    } else {
      CaseRpt.currentMDTRpt.medicalReports.push(entry);
    }
    setMedicalReports(CaseRpt.currentMDTRpt.medicalReports.slice());
    setShowPatientModal(false);
    setPatientForm(emptyPatientForm());
  }, [patientForm, setMedicalReports]);

  const openChecklistModal = useCallback((payload: { patient: any; checklistType: string }) => {
    const medRpt = CaseRpt.currentMDTRpt.medicalReports.find(
      (o: any) => o.tempid.toString() === payload.patient.tempid.toString());
    if (!medRpt) { App.showPopupNotification('Patient record not found.', 'error'); return; }
    setEditingMedicalReport(medRpt);
    setChecklistType(payload.checklistType);
    setChecklistForm(copyMedicalFieldsFromReport(medRpt));
    setShowChecklistModal(true);
  }, []);

  const saveChecklist = useCallback(() => {
    if (!editingMedicalReport) return;
    applyMedicalFieldsToReport(editingMedicalReport, checklistForm);
    setMedicalReports(CaseRpt.currentMDTRpt.medicalReports.slice());
    setShowChecklistModal(false);
    setEditingMedicalReport(null);
    setChecklistType('');
  }, [editingMedicalReport, checklistForm, setMedicalReports]);

  const renderRadioGroup = (field: string) => (
    <div className="radio-group">
      <label className="radio-inline">
        <input type="radio" checked={checklistForm[field] === 1} disabled={isReadOnly}
          onChange={() => setChecklistForm(f => ({ ...f, [field]: 1 }))} /><span>Yes</span>
      </label>
      <label className="radio-inline">
        <input type="radio" checked={checklistForm[field] === 2} disabled={isReadOnly}
          onChange={() => setChecklistForm(f => ({ ...f, [field]: 2 }))} /><span>No</span>
      </label>
      <label className="radio-inline">
        <input type="radio" checked={checklistForm[field] === 3} disabled={isReadOnly}
          onChange={() => setChecklistForm(f => ({ ...f, [field]: 3 }))} /><span>N/A</span>
      </label>
    </div>
  );

  return (
    <>
      <div className="tab-panel">
        <CaseReportMedicalList
          value={medicalReports}
          isReadOnly={isReadOnly}
          involvedPersons={involvedPersonOptions}
          onChange={(m: any[]) => { CaseRpt.currentMDTRpt.medicalReports = m; setMedicalReports(m); }}
          onAdd={() => openPatientModal()}
          onEdit={(item: any) => openPatientModal(item)}
          onOpenChecklist={openChecklistModal}
        />
      </div>

      {/* Patient Modal */}
      <ConfirmModal
        open={showPatientModal}
        title={patientForm.tempid ? 'Edit Patient' : 'Add Patient'}
        confirmText={patientForm.tempid ? 'Update Patient' : 'Add Patient'}
        confirmClass="primary"
        dialogClass="kstc-dialog--form"
        onConfirm={savePatient}
        onCancel={() => { setShowPatientModal(false); setPatientForm(emptyPatientForm()); }}
      >
        <div className="form-group">
          <label className="mandatory"><b>Patient</b></label>
          <Dropdownlist value={patientForm.personId} items={involvedPersonOptions}
            labelKey="name" valueKey="id" placeholder="Select patient..."
            onChange={(val) => {
              const p = involvedPersonOptions.find((o: any) => o.id === val);
              setPatientForm(f => ({ ...f, personId: val, personName: p?.name || '' }));
            }} />
        </div>
        <div className="form-group">
          <label className="mandatory"><b>Hospitalized?</b></label>
          <div className="radio-group">
            <label className="radio-inline"><input type="radio" checked={patientForm.hospitalized === '1'} onChange={() => setPatientForm(f => ({ ...f, hospitalized: '1' }))} /> Yes</label>
            <label className="radio-inline"><input type="radio" checked={patientForm.hospitalized === '2'} onChange={() => setPatientForm(f => ({ ...f, hospitalized: '2' }))} /> No</label>
            <label className="radio-inline"><input type="radio" checked={patientForm.hospitalized === '3'} onChange={() => setPatientForm(f => ({ ...f, hospitalized: '3' }))} /> N/A</label>
          </div>
        </div>
        <div className="form-group">
          <label><b>Symptoms or Injuries</b></label>
          <textarea className="form-control" value={patientForm.symptoms}
            onChange={e => setPatientForm(f => ({ ...f, symptoms: e.target.value }))} rows={5} />
        </div>
      </ConfirmModal>

      {/* Medical Checklist Modal */}
      <ConfirmModal
        open={showChecklistModal}
        title={checklistTitle}
        confirmText={isReadOnly ? undefined : 'Save'}
        confirmClass="primary"
        dialogClass="kstc-dialog--large"
        onConfirm={isReadOnly ? undefined : saveChecklist}
        onCancel={() => { setShowChecklistModal(false); setEditingMedicalReport(null); }}
      >
        <div className="medical-checklist-form kstc-body--scroll">
          {checklistSections.map((section: any) => (
            <div key={section.title} className="checklist-section">
              <h3 className="checklist-section-title">{section.title}</h3>
              {section.noteField ? (
                <div className="checklist-item">
                  <textarea className="form-control" value={checklistForm[section.noteField] || ''}
                    disabled={isReadOnly} rows={3} placeholder="Additional notes..."
                    onChange={e => setChecklistForm(f => ({ ...f, [section.noteField]: e.target.value }))} />
                </div>
              ) : (
                <>
                  {section.fields?.map((field: string) => (
                    <div key={field}>
                      <div className="checklist-item">
                        <label className="checklist-label">{MEDICAL_LABELS[field] || field}</label>
                        {renderRadioGroup(field)}
                      </div>
                      {section.conditionalTextFields?.[field] && checklistForm[field] === 1 && (
                        <div className="checklist-item checklist-item--indented">
                          <label className="checklist-label">If yes, please list:</label>
                          <textarea className="form-control" value={checklistForm[section.conditionalTextFields[field]] || ''}
                            disabled={isReadOnly} rows={2}
                            onChange={e => setChecklistForm(f => ({ ...f, [section.conditionalTextFields[field]]: e.target.value }))} />
                        </div>
                      )}
                    </div>
                  ))}
                  {section.hasLastAte && (
                    <div className="checklist-item">
                      <label className="checklist-label">When did you last eat?</label>
                      <DateTimePicker
                        value={checklistForm.lastAte ? new Date(checklistForm.lastAte) : null}
                        onChange={(val: Date | null) => setChecklistForm(f => ({ ...f, lastAte: val }))}
                        type="datetime"
                        format="DD MMM YYYY HH:mm"
                        clearable={true}
                        appendToBody={true}
                        disabled={isReadOnly}
                        style={{ maxWidth: '250px' }}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </ConfirmModal>
    </>
  );
});
